import React, { useEffect, useState } from 'react';
import axios from 'axios';
import Toast from '../../Shared/Toast';
import Tooltip from '../ItemTooltip';
import styles from './Mail.module.css';
import { formatDate, formatDateTime } from '../../../utils/dateFormat';

interface MailItem {
  id: number;
  senderId: number | null;
  senderName: string;
  subject: string;
  message: string;
  isRead: boolean;
  isAdmin: boolean;
  createdAt: string;
}

interface Friend {
  id: number;
  name: string;
  lastSeen: string | null;
  mercenary: {
    id: number;
    name: string;
    level: number;
    reputation: number;
    avatar: number;
    race: string;
    sex: string;
  };
}

interface SearchResult {
  id: number;
  name: string;
  type: 'user' | 'mercenary';
}

interface User extends SearchResult {
  username: string;
  displayName?: string;
}

interface Mercenary extends SearchResult {
  level: number;
  race: string;
  sex: string;
  avatar: number;
  userId: number;
}

const Mail: React.FC = () => {
    const decodeEntities = (value: string) => {
      const textarea = document.createElement('textarea');
      textarea.innerHTML = value;
      return textarea.value;
    };

    const renderSafeMailMessage = (value: string) => {
      const ITEM_TOKEN = /\[ITEM:(\d+):([^\]]+)\]/g;
      const lines = value
        .split(/<br\s*\/?>/gi)
        .map((line) => decodeEntities(line));

      return lines.map((line, lineIdx) => {
        const parts: React.ReactNode[] = [];
        let lastIdx = 0;
        let match: RegExpExecArray | null;
        ITEM_TOKEN.lastIndex = 0;
        while ((match = ITEM_TOKEN.exec(line)) !== null) {
          if (match.index > lastIdx) {
            parts.push(line.slice(lastIdx, match.index));
          }
          const itemId = parseInt(match[1], 10);
          const itemName = match[2];
          parts.push(
            <Tooltip key={`item-${lineIdx}-${itemId}-${match.index}`} itemId={itemId} style={{ display: 'inline-flex', verticalAlign: 'middle', height: 'auto' }}>
              <span className={styles.mailItemLink}>{itemName}</span>
            </Tooltip>
          );
          lastIdx = match.index + match[0].length;
        }
        if (lastIdx < line.length) {
          parts.push(line.slice(lastIdx));
        }
        return (
          <React.Fragment key={`mail-line-${lineIdx}`}>
            {parts}
            {lineIdx < lines.length - 1 && <br />}
          </React.Fragment>
        );
      });
    };

  const [inbox, setInbox] = useState<MailItem[]>([]);
  const [selectedMail, setSelectedMail] = useState<MailItem | null>(null);
  const [replyMessage, setReplyMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(true);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [totalPages, setTotalPages] = useState<number>(1);
  const [searchUsername, setSearchUsername] = useState<string>('');
  const [newMessage, setNewMessage] = useState<string>('');
  const [friends, setFriends] = useState<Friend[]>([]);
  const [showFriendsOverlay, setShowFriendsOverlay] = useState<boolean>(false);
  const [searchResults, setSearchResults] = useState<User[]>([]);
  const [selectedRecipientId, setSelectedRecipientId] = useState<number | null>(null);
  const [toastMessage, setToastMessage] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const [isComposing, setIsComposing] = useState<boolean>(false);
  const [selectMode, setSelectMode] = useState<boolean>(false);
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const mailsPerPage = 20;

  useEffect(() => {
    fetchInbox(currentPage);
  }, [currentPage]);

  const displayToast = (message: string, type: 'success' | 'error') => {
    setToastMessage(message);
    setToastType(type);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const fetchInbox = async (page: number) => {
    setLoading(true);
    try {
      const response = await axios.get('/api/mail/inbox', {
        params: { page, limit: mailsPerPage },
        withCredentials: true,
      });
      setInbox(response.data.inbox);
      setTotalPages(Math.max(1, Math.ceil((response.data.totalCount || 0) / mailsPerPage)));
    } catch (error) {
      displayToast('Error fetching inbox', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectMail = (mail: MailItem) => {
    setSelectedMail(mail);
    setReplyMessage('');
    if (!mail.isRead) {
      axios.post('/api/mail/mark-read', { mailId: mail.id }, { withCredentials: true })
        .then(() => {
          setInbox(prevInbox =>
            prevInbox.map(m =>
              m.id === mail.id ? { ...m, isRead: true } : m
            )
          );
        })
        .catch(error => displayToast('Error marking mail as read', 'error'));
    }
  };

  const handleReply = async () => {
    if (selectedMail && replyMessage.trim()) {
      try {
        await axios.post(
          '/api/mail/reply',
          {
            mailId: selectedMail.id,
            senderId: selectedMail.senderId,
            message: replyMessage,
          },
          { withCredentials: true }
        );
        setReplyMessage('');
        displayToast('Reply sent successfully', 'success');
      } catch (error) {
        displayToast('Failed to send reply', 'error');
      }
    }
  };

  const handleSendMessage = async () => {
    if ((selectedRecipientId || searchUsername.trim()) && newMessage.trim()) {
      try {
        await axios.post(
          '/api/mail/send',
          selectedRecipientId
            ? { receiverId: selectedRecipientId, subject: 'New Message', message: newMessage }
            : { recipientName: searchUsername, subject: 'New Message', message: newMessage },
          { withCredentials: true }
        );
        displayToast('Message sent successfully', 'success');
        setSearchUsername('');
        setNewMessage('');
        setSearchResults([]);
        setSelectedRecipientId(null);
      } catch (error) {
        displayToast('Failed to send message', 'error');
      }
    }
  };

  const fetchFriends = async () => {
    try {
      const response = await axios.get('/api/friend/list', { withCredentials: true });
      setFriends(response.data.friends);
    } catch (error) {
      displayToast('Error fetching friends', 'error');
    }
  };

  const searchUsernames = async (username: string) => {
    try {
      const response = await axios.get(`/api/mail/search?username=${username}`, { withCredentials: true });
      const users = (response.data.users || []).map((user: User) => ({ ...user, type: 'user' }));
      const mercenaries = (response.data.mercenaries || []).map((mercenary: Mercenary) => ({
        ...mercenary,
        type: 'mercenary',
        name: mercenary.name,
      }));
      
      setSearchResults([...users, ...mercenaries]);
    } catch (error) {
      displayToast('Error searching for usernames or mercenaries', 'error');
      setSearchResults([]);
    }
  };

  const handleDeleteMail = async (mailId: number) => {
    try {
      await axios.post('/api/mail/delete', { mailId }, { withCredentials: true });
      setInbox(prevInbox => prevInbox.filter(mail => mail.id !== mailId));
      if (selectedMail?.id === mailId) setSelectedMail(null);
      displayToast('Mail deleted successfully', 'success');
    } catch (error) {
      displayToast('Failed to delete mail', 'error');
    }
  };

  const toggleSelectMode = () => {
    setSelectMode(prev => !prev);
    setSelectedIds(new Set());
  };

  const toggleItemSelection = (id: number) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const selectAll = () => setSelectedIds(new Set(inbox.map(m => m.id)));
  const deselectAll = () => setSelectedIds(new Set());

  const handleBulkDelete = async () => {
    if (selectedIds.size === 0) return;
    try {
      await Promise.all(
        Array.from(selectedIds).map(id =>
          axios.post('/api/mail/delete', { mailId: id }, { withCredentials: true })
        )
      );
      const deleted = new Set(selectedIds);
      setInbox(prev => prev.filter(m => !deleted.has(m.id)));
      if (selectedMail && deleted.has(selectedMail.id)) setSelectedMail(null);
      displayToast(`${deleted.size} message${deleted.size === 1 ? '' : 's'} deleted`, 'success');
      setSelectedIds(new Set());
      setSelectMode(false);
    } catch {
      displayToast('Failed to delete some messages', 'error');
    }
  };

  const openFriendsOverlay = () => {
    fetchFriends();
    setShowFriendsOverlay(true);
  };

  const selectRecipientFromFriendList = (id: number, name: string) => {
    setSelectedRecipientId(id);
    setSearchUsername(name);
    setShowFriendsOverlay(false);
  };

  const selectRecipientFromSearch = (id: number, displayText: string) => {
    setSelectedRecipientId(id);
    setSearchUsername(displayText);
    setSearchResults([]);
  };

  const handleCompose = () => {
    setIsComposing(true);
    setSelectedMail(null);
    setSearchUsername('');
    setNewMessage('');
    setSelectedRecipientId(null);
    setSelectMode(false);
    setSelectedIds(new Set());
  };

  const handleCancelCompose = () => {
    setIsComposing(false);
    setSearchUsername('');
    setNewMessage('');
    setSearchResults([]);
    setSelectedRecipientId(null);
  };

  const goToPage = (page: number) => {
    if (page > 0 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  return (
    <div className={styles.mailContainer}>
      <div className={styles.mailHeader}>
        <h1>Mail</h1>
        <button 
          onClick={handleCompose} 
          className={styles.composeButton}
          disabled={isComposing}
        >
          ✉ Compose New Message
        </button>
      </div>

      {toastMessage && (
        <Toast
          message={toastMessage}
          type={toastType}
          duration={3000}
          onClose={() => setToastMessage(null)}
        />
      )}

      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className={styles.inboxContainer}>
          <div className={styles.inboxList}>
            <div className={styles.inboxHeader}>
              <h2>Inbox</h2>
              <div className={styles.inboxHeaderActions}>
                <span className={styles.mailCount}>{inbox.length} messages</span>
                {inbox.length > 0 && (
                  <button onClick={toggleSelectMode} className={styles.selectModeBtn}>
                    {selectMode ? '✕ Cancel' : '☑ Select'}
                  </button>
                )}
              </div>
            </div>
            {selectMode && inbox.length > 0 && (
              <div className={styles.bulkBar}>
                <label className={styles.selectAllLabel}>
                  <input
                    type="checkbox"
                    checked={selectedIds.size === inbox.length && inbox.length > 0}
                    onChange={selectedIds.size === inbox.length ? deselectAll : selectAll}
                  />
                  <span>Select all ({inbox.length})</span>
                </label>
                <button
                  className={styles.bulkDeleteBtn}
                  onClick={handleBulkDelete}
                  disabled={selectedIds.size === 0}
                >
                  🗑 Delete ({selectedIds.size})
                </button>
              </div>
            )}
            {inbox.length > 0 ? (
              <ul>
                {inbox.map((mail) => (
                  <li
                    key={mail.id}
                    onClick={() => {
                      if (selectMode) {
                        toggleItemSelection(mail.id);
                      } else {
                        handleSelectMail(mail);
                        setIsComposing(false);
                      }
                    }}
                    className={`${styles.mailItem} ${mail.isRead ? styles.read : styles.unread} ${!selectMode && selectedMail?.id === mail.id ? styles.selected : ''} ${selectMode && selectedIds.has(mail.id) ? styles.checked : ''}`}
                  >
                    {selectMode && (
                      <input
                        type="checkbox"
                        readOnly
                        checked={selectedIds.has(mail.id)}
                        onClick={(e) => e.stopPropagation()}
                        className={styles.mailCheckbox}
                      />
                    )}
                    <div className={styles.mailItemHeader}>
                      <span className={styles.sender}>{mail.isAdmin ? '👑 Admin' : mail.senderName}</span>
                      <span className={styles.date}>{formatDate(mail.createdAt)}</span>
                    </div>
                    <div className={styles.subject}>{mail.subject}</div>
                    {!selectMode && (
                      <button 
                        onClick={(e) => { e.stopPropagation(); handleDeleteMail(mail.id); }} 
                        className={styles.deleteButton}
                        title="Delete message"
                      >
                        🗑️
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            ) : (
              <p className={styles.emptyInbox}>No messages in your inbox.</p>
            )}
            <div className={styles.pagination}>
              <button onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>Previous</button>
              <span>
                Page {currentPage} of {totalPages ?? 1}
              </span>
              <button onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>Next</button>
            </div>
          </div>

          <div className={styles.mailViewer}>
            {selectedMail && !isComposing ? (
              <>
                <div className={styles.messageView}>
                  <div className={styles.messageHeader}>
                    <div>
                      <h2>{selectedMail.subject}</h2>
                      <p className={styles.messageFrom}>From: {selectedMail.isAdmin ? '👑 Admin' : selectedMail.senderName}</p>
                    </div>
                    <button
                      onClick={() => setSelectedMail(null)}
                      className={styles.closeButton}
                      title="Close"
                    >
                      ✖
                    </button>
                  </div>
                  <div className={styles.messageBody}>
                    <p>{renderSafeMailMessage(selectedMail.message)}</p>
                  </div>
                </div>
                {!selectedMail.isAdmin && selectedMail.senderId !== null && (
                  <div className={styles.replySection}>
                    <h3>Reply</h3>
                    <textarea
                      placeholder="Write your reply..."
                      className={styles.replyTextarea}
                      value={replyMessage}
                      onChange={(e) => setReplyMessage(e.target.value)}
                    />
                    <button onClick={handleReply} className={styles.sendButton} disabled={!replyMessage.trim()}>
                      Send Reply
                    </button>
                  </div>
                )}
              </>
            ) : isComposing ? (
              <div className={styles.composeSection}>
                <div className={styles.composeHeader}>
                  <h2>New Message</h2>
                  <button onClick={handleCancelCompose} className={styles.cancelButton}>
                    ✖ Cancel
                  </button>
                </div>
                
                <div className={styles.composeForm}>
                  <div className={styles.recipientRow}>
                    <label>To:</label>
                    <div className={styles.recipientInputContainer}>
                      <input
                        type="text"
                        placeholder="Search for username or mercenary name..."
                        className={styles.recipientInput}
                        value={searchUsername}
                        onChange={(e) => {
                          setSearchUsername(e.target.value);
                          setSelectedRecipientId(null);
                          searchUsernames(e.target.value);
                        }}
                      />
                      <button 
                        onClick={openFriendsOverlay}
                        className={styles.friendsButton}
                        title="Select from friends"
                      >
                        👥 Friends
                      </button>
                    </div>
                    {searchUsername.trim() && searchResults.length > 0 && (
                      <ul className={styles.searchDropdown}>
                        {searchResults.map(result => (
                          <li
                            key={result.id}
                            onClick={() => selectRecipientFromSearch(
                              result.type === 'user'
                                ? (result as User).id
                                : (result as unknown as Mercenary).userId,
                              result.type === 'user' ? (result as User).username : result.name
                            )}
                            className={styles.searchResultItem}
                          >
                            {result.type === 'user' ? (
                              <span>👤 {result.username}</span>
                            ) : (
                              <span>⚔️ {result.name}</span>
                            )}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                  
                  <div className={styles.messageRow}>
                    <label>Message:</label>
                    <textarea
                      placeholder="Write your message..."
                      className={styles.composeTextarea}
                      value={newMessage}
                      onChange={(e) => setNewMessage(e.target.value)}
                    />
                  </div>
                  
                  <button 
                    onClick={handleSendMessage} 
                    className={styles.sendButtonLarge} 
                    disabled={(!selectedRecipientId && !searchUsername.trim()) || !newMessage.trim()}
                  >
                    📤 Send Message
                  </button>
                </div>
              </div>
            ) : (
              <div className={styles.emptyState}>
                <div className={styles.emptyStateIcon}>📬</div>
                <h3>No message selected</h3>
                <p>Select a message from your inbox or compose a new one</p>
              </div>
            )}
          </div>
        </div>
      )}

      {showFriendsOverlay && (
        <div className={styles.overlay}>
          <div className={styles.overlayContent}>
            <h2>Select a Friend</h2>
            <button onClick={() => setShowFriendsOverlay(false)} className={styles.closeOverlayButton}>Close</button>
            <ul>
              {friends.map(friend => (
                <li key={friend.id} onClick={() => selectRecipientFromFriendList(friend.id, friend.name)}>
                  {friend.name} (Last seen: {friend.lastSeen ? formatDateTime(friend.lastSeen) : 'Offline'})
                </li>
              ))}
            </ul>
          </div>
        </div>
      )}
    </div>
  );
};

export default Mail;
